import { useCallback, useEffect, useState } from 'react';
import { type AudioDevice, listAudioDevices, realDevices } from './devices';

/**
 * The speakers rehearsal mode plays through, kept in this browser: rehearsal runs on the room's
 * computer, which is not where a chorist did their headset check. Practice keeps using the
 * headset check's output. The header's audio menu chooses them; the rehearsal page applies them.
 */
export interface SpeakerChoice {
  deviceId: string;
  /** The device's name when it was chosen, to find it again if the browser renews its id. */
  label: string;
}

const KEY = 'corocanto.rehearsalSpeaker';
const listeners = new Set<() => void>();

export function loadSpeaker(): SpeakerChoice | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<SpeakerChoice>;
    return typeof value.deviceId === 'string' ? { deviceId: value.deviceId, label: value.label ?? '' } : null;
  } catch {
    return null;
  }
}

let current: SpeakerChoice | null | undefined;

/** Remember the speakers; null goes back to the system default. An open rehearsal page follows at once. */
export function saveSpeaker(choice: SpeakerChoice | null): void {
  current = choice;
  try {
    if (choice) localStorage.setItem(KEY, JSON.stringify(choice));
    else localStorage.removeItem(KEY);
  } catch {
    // Storage blocked: the choice lasts until the page closes.
  }
  for (const listener of listeners) listener();
}

function currentSpeaker(): SpeakerChoice | null {
  if (current === undefined) current = loadSpeaker();
  return current;
}

/**
 * The output id to play through: the chosen speakers while they are connected (by id, or by name
 * if the id changed), otherwise '' for the system default. `missing` says the chosen ones are gone.
 */
export function speakerToUse(choice: SpeakerChoice | null, outputs: readonly AudioDevice[]): { deviceId: string; missing: boolean } {
  if (!choice) return { deviceId: '', missing: false };
  const byId = outputs.find((d) => d.deviceId === choice.deviceId);
  if (byId) return { deviceId: byId.deviceId, missing: false };
  const byName = choice.label ? outputs.find((d) => d.label === choice.label) : undefined;
  if (byName) return { deviceId: byName.deviceId, missing: false };
  return { deviceId: '', missing: true };
}

/** The audio outputs (with Chrome's "default" alias), read again when devices change. `refresh` reads them now. */
export function useOutputDevices(): { outputs: AudioDevice[]; refresh: () => void } {
  const [outputs, setOutputs] = useState<AudioDevice[]>([]);
  const refresh = useCallback(() => {
    void listAudioDevices()
      .then(({ outputs: list }) => setOutputs(list))
      .catch(() => setOutputs([]));
  }, []);
  useEffect(() => {
    refresh();
    navigator.mediaDevices?.addEventListener?.('devicechange', refresh);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', refresh);
  }, [refresh]);
  return { outputs, refresh };
}

/** The rehearsal speakers: the saved choice, the outputs, and the device to play through. */
export function useRehearsalSpeaker() {
  const [choice, setChoice] = useState<SpeakerChoice | null>(() => currentSpeaker());
  useEffect(() => {
    const update = () => setChoice(currentSpeaker());
    listeners.add(update);
    return () => {
      listeners.delete(update);
    };
  }, []);
  const { outputs, refresh } = useOutputDevices();
  const real = realDevices(outputs);
  const { deviceId, missing } = speakerToUse(choice, real);
  return { choice, outputs, real, deviceId, missing, refresh };
}
